import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as PlusIcon } from '@blink/assets/src/icons/plus.svg';
import { ReactComponent as ClockIcon } from '@blink/assets/src/icons/clock.svg';
import { ReactComponent as LoaderDash } from '@blink/assets/src/icons/loaderDash.svg';

import { useTeamStyle } from './Team.style';

import { showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import { TeamSection } from '@/features/Team/ui/TeamSection/ui';
import { useTeamSection } from '@/features/Team/ui/TeamSection/hooks/useTeamSection';
import { Store } from '@/shared/store';
import { Button, TooltipWrapperDisabled } from '@/shared/ui';
import { MemberRole } from '@/shared/api/organization';
import { EmptyBanner } from '@/widgets/Banners';

export const Team: FC = () => {
    const classes = useTeamStyle();
    const dispatch = useDispatch();

    const { info: organizationInfo, restriction } = useSelector(
        (state: Store) => state.organization,
    );

    const {
        emailsList,
        organizationMembersLoading,
        fetchOrganizationMembers,
        filteredMembers,
        organizationMembers,
        ...rest
    } = useTeamSection();

    const currentMember = organizationMembers.find(
        (member) => member.email === organizationInfo?.email,
    );
    const disableOperations = currentMember?.role !== MemberRole.ADMIN;

    const handleInviteMembers = () => {
        return dispatch(
            showModalAction({
                modalName: ModalName.INVITE_MEMBERS_MODAL,
                modalTitle: 'Invite Members',
                modalProps: {
                    description: 'Send them an invitation via email.',
                    emails: emailsList,
                    refresh: fetchOrganizationMembers,
                },
            }),
        );
    };

    const getContent = useCallback(() => {
        if (restriction) {
            return (
                <EmptyBanner
                    customTitle='You don’t have permission to view organization members'
                    customSubtitle='Please check back later or contact support if you need help'
                    icon={ClockIcon}
                    className='!w-full'
                />
            );
        } else if (organizationMembers.length === 0 && !organizationMembersLoading) {
            return (
                <EmptyBanner
                    customTitle='You don’t have any organization members yet.'
                    customSubtitle='Please check back later or contact support if you need help'
                    icon={ClockIcon}
                    className='!w-full'
                />
            );
        } else {
            return (
                <TeamSection
                    organizationMembersLoading={organizationMembersLoading}
                    filteredMembers={filteredMembers}
                    disableOperations={disableOperations}
                    {...rest}
                />
            );
        }
    }, [
        restriction,
        organizationMembers,
        organizationMembersLoading,
        filteredMembers,
        disableOperations,
        rest,
    ]);

    return (
        <div>
            <div className={classes.top}>
                <div className={classes.heading}>
                    <div className={classes.title}>
                        <div
                            className={classes.titleText}
                        >{`${organizationInfo.organization.name} Overview`}</div>
                        <div className={classes.description}>
                            Manage your organizations and their members.
                        </div>
                    </div>
                    {disableOperations ? (
                        <TooltipWrapperDisabled
                            component={
                                <Button
                                    leftIcon={
                                        organizationMembersLoading ? <LoaderDash /> : <PlusIcon />
                                    }
                                    variant={Button.types.Primary}
                                    onClick={handleInviteMembers}
                                    className='min-w-[162px]'
                                    name={'invite-members'}
                                    disabled={organizationMembersLoading || disableOperations}
                                >
                                    {organizationMembersLoading ? 'Loading...' : 'Invite Members'}
                                </Button>
                            }
                        />
                    ) : (
                        <Button
                            leftIcon={organizationMembersLoading ? <LoaderDash /> : <PlusIcon />}
                            variant={Button.types.Primary}
                            onClick={handleInviteMembers}
                            className='min-w-[162px]'
                            name={'invite-members'}
                            disabled={organizationMembersLoading || disableOperations}
                        >
                            {organizationMembersLoading ? 'Loading...' : 'Invite Members'}
                        </Button>
                    )}
                </div>
            </div>

            <div className={classes.top}>
                <div className={classes.content}>
                    <div className={classes.main}>
                        <div className={classes.contentWrapper}>{getContent()}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};
